import React, { useState } from "react";
import {
  Box,
  Text,
  VStack,
  Card,
  CardBody,
  Heading,
  Center,
  List,
  ListItem,
  ListIcon,
  IconButton,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Code,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  HStack,
  Divider,
} from "@chakra-ui/react";
import {
  MdCheckCircle,
  MdBugReport,
  MdLightbulb,
  MdWarning,
  MdStar,
} from "react-icons/md";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import ReactMarkdown from "react-markdown";

export default function ArcteryxAnalysisView({ conversationData }) {
  const analysisData = conversationData?.analysis;
  const rawData = conversationData;
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [showSegments, setShowSegments] = useState(false);
  const [showMarkdownReport, setShowMarkdownReport] = useState(false);

  // Phase display names for Arc'teryx
  const phaseNames = {
    Connect: "CONNECT",
    Educate: "EDUCATE",
    Recommend: "RECOMMEND",
  };

  // Phase colors for Arc'teryx
  const phaseColors = {
    Connect: "blue",
    Educate: "green",
    Recommend: "purple",
  };

  // Helper function to get score color (1-3 scale)
  const getScoreColor = (score) => {
    if (score >= 3) return "green";
    if (score >= 2) return "orange";
    return "red";
  };

  // Helper function to get score badge text
  const getScoreBadge = (score) => {
    if (score >= 3) return "Excellent";
    if (score >= 2) return "Good";
    return "Needs Work";
  };

  // Helper function to get emoji for score
  const getScoreEmoji = (score) => {
    if (score >= 3) return "⭐";
    if (score >= 2) return "🟢";
    return "🔴";
  };

  // Helper function to extract transcript text
  const getTranscriptText = (transcriptData) => {
    if (!transcriptData) return "";
    if (transcriptData.transcription) return transcriptData.transcription;
    if (transcriptData.result) return transcriptData.result;
    return "";
  };

  // Debug drawer component
  const DebugDrawer = () => (
    <Drawer
      isOpen={isDebugOpen}
      placement="right"
      onClose={() => setIsDebugOpen(false)}
      size="lg"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Debug Information</DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <Card>
              <CardBody>
                <Heading size="sm" mb={2}>
                  Raw Conversation Data
                </Heading>
                <Button
                  size="sm"
                  leftIcon={
                    showSegments ? <ChevronDownIcon /> : <ChevronRightIcon />
                  }
                  onClick={() => setShowSegments(!showSegments)}
                  variant="ghost"
                  mb={2}
                >
                  {showSegments ? "Hide" : "Show"} Full Data
                </Button>
                <Code display="block" whiteSpace="pre" overflow="auto" p={2}>
                  {JSON.stringify(
                    showSegments ? rawData : analysisData,
                    null,
                    2
                  )}
                </Code>
              </CardBody>
            </Card>

            {rawData?.raw_transcript_data && (
              <Card>
                <CardBody>
                  <Heading size="sm" mb={2}>
                    Raw Transcript
                  </Heading>
                  <Text
                    whiteSpace="pre-wrap"
                    p={2}
                    bg="gray.50"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    {getTranscriptText(rawData.raw_transcript_data)}
                  </Text>
                </CardBody>
              </Card>
            )}

            {rawData?.filtered_transcript_data && (
              <Card>
                <CardBody>
                  <Heading size="sm" mb={2}>
                    Filtered Transcript
                  </Heading>
                  <Text
                    whiteSpace="pre-wrap"
                    p={2}
                    bg="gray.50"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    {getTranscriptText(rawData.filtered_transcript_data)}
                  </Text>
                </CardBody>
              </Card>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  if (!analysisData || !analysisData.section_feedback) {
    return (
      <Center h="100vh">
        <Text>No Arc'teryx analysis data available</Text>
      </Center>
    );
  }

  // Calculate overall performance
  const totalScore = analysisData.section_feedback.reduce(
    (sum, section) => sum + section.score,
    0
  );
  const maxScore = analysisData.section_feedback.length * 3;
  const overallPercentage = (totalScore / maxScore) * 100;

  return (
    <Box p={5} maxW="1200px" mx="auto" position="relative">
      {/* Debug Button */}
      <IconButton
        icon={<MdBugReport />}
        position="fixed"
        right="4"
        top="4"
        colorScheme="gray"
        onClick={() => setIsDebugOpen(true)}
        aria-label="Debug View"
        title="Open Debug View"
      />

      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading mb={2}>Arc'teryx Sales Analysis</Heading>
          <Text color="gray.600">
            Performance evaluation based on the Arc'teryx CER framework
          </Text>
        </Box>

        {/* Overall Score Summary */}
        <Card borderWidth="2px" borderColor="blue.200" bg="blue.50">
          <CardBody>
            <Heading size="md" mb={4}>
              Overall Performance
            </Heading>
            <Text mb={4} fontSize="lg" fontWeight="bold">
              {getScoreEmoji(Math.round(overallPercentage / 33.33))}{" "}
              {totalScore}/{maxScore} ({overallPercentage.toFixed(1)}%)
            </Text>
            <HStack spacing={8} wrap="wrap">
              {analysisData.section_feedback.map((section) => (
                <VStack key={section.phase} align="center" spacing={2}>
                  <Text fontWeight="bold" fontSize="sm">
                    {section.phase}
                  </Text>
                  <Text fontSize="2xl">{getScoreEmoji(section.score)}</Text>
                  <Box
                    width="100px"
                    height="24px"
                    bg="gray.200"
                    borderRadius="md"
                    overflow="hidden"
                  >
                    <Box
                      width={`${(section.score / 3) * 100}%`}
                      height="100%"
                      bg={
                        section.score >= 3
                          ? "green.500"
                          : section.score >= 2
                          ? "orange.400"
                          : "red.500"
                      }
                      transition="width 0.3s ease"
                    />
                  </Box>
                  <Badge colorScheme={getScoreColor(section.score)}>
                    {section.score}/3
                  </Badge>
                </VStack>
              ))}
            </HStack>
          </CardBody>
        </Card>

        {/* Final Report Toggle */}
        {analysisData.final_report_md && (
          <Card>
            <CardBody>
              <Button
                leftIcon={
                  showMarkdownReport ? (
                    <ChevronDownIcon />
                  ) : (
                    <ChevronRightIcon />
                  )
                }
                onClick={() => setShowMarkdownReport(!showMarkdownReport)}
                variant="ghost"
                mb={showMarkdownReport ? 4 : 0}
              >
                {showMarkdownReport ? "Hide" : "Show"} Full Analysis Report
              </Button>
              {showMarkdownReport && (
                <Box
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  maxH="500px"
                  overflowY="auto"
                >
                  <ReactMarkdown>{analysisData.final_report_md}</ReactMarkdown>
                </Box>
              )}
            </CardBody>
          </Card>
        )}

        {/* Phase Details */}
        <Accordion allowMultiple defaultIndex={[0]}>
          {analysisData.section_feedback.map((section) => {
            const phaseColor = phaseColors[section.phase] || "gray";

            return (
              <AccordionItem key={section.phase}>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <Text fontSize="2xl">
                          {getScoreEmoji(section.score)}
                        </Text>
                        <Heading size="md">{section.phase}</Heading>
                        <Badge
                          colorScheme={getScoreColor(section.score)}
                          fontSize="md"
                          px={3}
                          py={1}
                        >
                          {section.score}/3 - {getScoreBadge(section.score)}
                        </Badge>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={4}>
                    {/* Conversation Segments */}
                    {section.segments && section.segments.length > 0 && (
                      <Card bg="gray.50">
                        <CardBody>
                          <Heading size="sm" mb={3}>
                            Conversation Segments
                          </Heading>
                          <VStack align="stretch" spacing={2}>
                            {section.segments.map((segment, idx) => (
                              <Text
                                key={idx}
                                fontSize="sm"
                                fontStyle="italic"
                                p={2}
                                bg="white"
                                borderRadius="md"
                                borderLeft="3px solid"
                                borderLeftColor={`${phaseColor}.400`}
                              >
                                {segment}
                              </Text>
                            ))}
                          </VStack>
                        </CardBody>
                      </Card>
                    )}

                    {/* Strengths */}
                    {section.strengths && section.strengths.length > 0 && (
                      <Card borderColor="green.200" borderWidth="1px">
                        <CardBody>
                          <HStack mb={3}>
                            <MdStar color="green" />
                            <Heading size="sm">Strengths</Heading>
                          </HStack>
                          <List spacing={2}>
                            {section.strengths.map((strength, idx) => (
                              <ListItem key={idx}>
                                <ListIcon
                                  as={MdCheckCircle}
                                  color="green.500"
                                />
                                <Text as="span" fontSize="sm">
                                  {strength}
                                </Text>
                              </ListItem>
                            ))}
                          </List>
                        </CardBody>
                      </Card>
                    )}

                    {/* Opportunities */}
                    {section.opportunities &&
                      section.opportunities.length > 0 && (
                        <Card borderColor="orange.200" borderWidth="1px">
                          <CardBody>
                            <HStack mb={3}>
                              <MdWarning color="orange" />
                              <Heading size="sm">
                                Opportunities for Growth
                              </Heading>
                            </HStack>
                            <List spacing={2}>
                              {section.opportunities.map((opportunity, idx) => (
                                <ListItem key={idx}>
                                  <ListIcon
                                    as={MdLightbulb}
                                    color="orange.500"
                                  />
                                  <Text as="span" fontSize="sm">
                                    {opportunity}
                                  </Text>
                                </ListItem>
                              ))}
                            </List>
                          </CardBody>
                        </Card>
                      )}

                    {/* Practice Drill */}
                    {section.micro_drill && (
                      <Card
                        bg="purple.50"
                        borderColor="purple.200"
                        borderWidth="1px"
                      >
                        <CardBody>
                          <HStack mb={2}>
                            <Text fontSize="lg">🎯</Text>
                            <Heading size="sm">Practice Drill</Heading>
                          </HStack>
                          <Text fontSize="sm" fontWeight="medium">
                            {section.micro_drill}
                          </Text>
                        </CardBody>
                      </Card>
                    )}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </VStack>

      {/* Debug Drawer */}
      <DebugDrawer />
    </Box>
  );
}
